import { useArenaStore } from "../../stores/useArenaStore";
import { ARROW_TYPES, playerDamage } from "@shared/constants";
import type { ArenaState } from "@shared/types";

interface HUDProps {
  state: ArenaState;
}

export function HUD({ state }: HUDProps) {
  const { playerId } = useArenaStore();
  const me = state.players.find((p) => p.id === playerId);
  if (!me) return null;

  const hpPct = (me.hp / me.maxHp) * 100;
  const xpPct = (me.xp / me.xpToNext) * 100;
  const dmg = playerDamage(me);
  const arrowSpec = ARROW_TYPES[me.arrowType];
  const fame = me.kills * 2 + me.level * 3;

  return (
    <div className="pointer-events-none absolute bottom-4 left-4 w-72 space-y-2">
      {/* HP bar */}
      <div className="rounded-xl border border-archer-700 bg-archer-900/90 p-3 backdrop-blur">
        <div className="mb-1 flex items-center justify-between text-sm">
          <span className="font-bold text-white">{me.name}</span>
          <span className="text-archer-300">
            Lv.{me.level} · {me.kills}☠
          </span>
        </div>
        <div className="h-3 overflow-hidden rounded-full bg-archer-950">
          <div
            className="h-full rounded-full bg-gradient-to-r from-red-500 to-green-500 transition-all duration-300"
            style={{ width: `${hpPct}%` }}
          />
        </div>
        <div className="mt-0.5 text-right text-xs text-archer-300">
          {Math.ceil(me.hp)} / {me.maxHp}
        </div>

        {/* XP bar */}
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-archer-950">
          <div
            className="h-full rounded-full bg-dawn-500 transition-all duration-300"
            style={{ width: `${xpPct}%` }}
          />
        </div>
        <div className="mt-0.5 text-right text-[10px] text-archer-500">
          {me.xp} / {me.xpToNext} XP
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-2">
        <div className="flex-1 rounded-xl border border-archer-700 bg-archer-900/90 p-2 text-center backdrop-blur">
          <div className="text-[10px] uppercase tracking-wide text-archer-500">Damage</div>
          <div className="text-lg font-bold text-dawn-400">{Math.round(dmg)}</div>
        </div>
        <div className="flex-1 rounded-xl border border-archer-700 bg-archer-900/90 p-2 text-center backdrop-blur">
          <div className="text-[10px] uppercase tracking-wide text-archer-500">Fame</div>
          <div className="text-lg font-bold text-dusk-400">{fame}</div>
        </div>
        <div className="flex-1 rounded-xl border border-archer-700 bg-archer-900/90 p-2 text-center backdrop-blur">
          <div className="text-[10px] uppercase tracking-wide text-archer-500">Arrow</div>
          <div
            className="text-lg font-bold"
            style={{ color: `#${arrowSpec.color.toString(16).padStart(6, "0")}` }}
          >
            {arrowSpec.name}
          </div>
        </div>
      </div>

      <div className="text-center text-[10px] text-archer-500">
        Press <kbd className="rounded bg-archer-800 px-1">E</kbd> for upgrades
      </div>
    </div>
  );
}
